import React, { useRef, useState } from "react";
import { LocationEntityType } from "@/lib/supabase/supabaseTypes";
import { useDesktopsStore } from "@/dentlab/src/store/Desktops";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import { MoreVertical, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/dentlab/src/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/dentlab/src/components/ui/dropdown-menu";

interface LocationTabsProps {
	locations: LocationEntityType[];
	selectedLocationId: number | null;
	onSelectLocation: (id: number) => void;
	organizationId: string;
}

export const LocationTabs: React.FC<LocationTabsProps> = ({
	locations,
	selectedLocationId,
	onSelectLocation,
	organizationId,
}) => {
	const {
		createLocation,
		deleteLocation,
		updateLocation,
		setDefaultLocation,
	} = useDesktopsStore((state) => ({
		createLocation: state.createLocation,
		deleteLocation: state.deleteLocation,
		updateLocation: state.updateLocation,
		setDefaultLocation: state.setDefaultLocation,
	}));

	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [newLocationName, setNewLocationName] = useState("");
	const [editingLocationId, setEditingLocationId] = useState<number | null>(
		null
	);
	const [editingName, setEditingName] = useState("");
	const editInputRef = useRef<HTMLInputElement>(null);

	const handleAddLocation = async () => {
		if (!newLocationName.trim()) return;
		const created = await createLocation({
			organization_id: organizationId,
			name: newLocationName.trim(),
		});
		if (created) {
			onSelectLocation(created.id);
			setAddDialogOpen(false);
			setNewLocationName("");
		}
	};

	const handleDeleteLocation = async (locationId: number) => {
		const location = locations.find((l) => l.id === locationId);
		if (location?.is_default) {
			showNotification({
				message: "Standard-Standort kann nicht gelöscht werden",
				type: "warning",
			});
			return;
		}
		await deleteLocation(locationId);
		if (selectedLocationId === locationId) {
			const defaultLoc = locations.find((l) => l.is_default);
			if (defaultLoc) onSelectLocation(defaultLoc.id);
		}
	};

	const handleStartRename = (location: LocationEntityType) => {
		setEditingLocationId(location.id);
		setEditingName(location.name);
		setTimeout(() => editInputRef.current?.select(), 0);
	};

	const handleConfirmRename = async () => {
		if (!editingLocationId || !editingName.trim()) return;
		const current = locations.find((l) => l.id === editingLocationId);
		if (current && current.name !== editingName.trim()) {
			await updateLocation(editingLocationId, {
				name: editingName.trim(),
			});
		}
		setEditingLocationId(null);
	};

	const handleSetDefault = async (locationId: number) => {
		await setDefaultLocation(locationId, organizationId);
	};

	return (
		<div>
			<div className="flex items-center justify-between mb-2">
				<h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
					Standorte
				</h2>
				<Button
					variant="outline"
					size="sm"
					onClick={() => setAddDialogOpen(true)}
					aria-label="Standort hinzufuegen"
					data-testid="location-add-button"
				>
					<Plus className="h-4 w-4 mr-1" />
					Standort
				</Button>
			</div>
			<div className="flex gap-2 flex-wrap">
				{locations.map((location) => (
					<div
						key={location.id}
						className="relative group flex items-center"
					>
						{editingLocationId === location.id ? (
							<Input
								ref={editInputRef}
								value={editingName}
								onChange={(e) => setEditingName(e.target.value)}
								onBlur={handleConfirmRename}
								onKeyDown={(e) => {
									if (e.key === "Enter")
										handleConfirmRename();
									if (e.key === "Escape")
										setEditingLocationId(null);
								}}
								className="h-9 w-40 text-sm"
								autoFocus
								data-testid={`location-rename-input-${location.id}`}
							/>
						) : (
							<button
								onClick={() => onSelectLocation(location.id)}
								className={cn(
									"px-4 py-2 rounded-md text-sm font-medium transition-colors pr-8",
									selectedLocationId === location.id
										? "bg-primary text-white"
										: "bg-gray-100 text-gray-700 hover:bg-gray-200"
								)}
								data-testid={`location-tab-${location.id}`}
								aria-label={location.name}
							>
								{location.name}
								{location.is_default && (
									<span className="ml-1.5 text-xs opacity-70">
										(Standard)
									</span>
								)}
							</button>
						)}
						{editingLocationId !== location.id && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<button
										className={cn(
											"absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity",
											selectedLocationId === location.id
												? "text-white/70 hover:text-white"
												: "text-gray-400 hover:text-gray-600"
										)}
										aria-label={`Standort ${location.name} bearbeiten`}
										data-testid={`location-menu-${location.id}`}
									>
										<MoreVertical className="h-3.5 w-3.5" />
									</button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start">
									<DropdownMenuItem
										onClick={() =>
											handleStartRename(location)
										}
										data-testid={`location-rename-${location.id}`}
									>
										<Pencil className="h-4 w-4 mr-2" />
										Umbenennen
									</DropdownMenuItem>
									{!location.is_default && (
										<>
											<DropdownMenuItem
												onClick={() =>
													handleSetDefault(
														location.id
													)
												}
												data-testid={`location-set-default-${location.id}`}
											>
												<Star className="h-4 w-4 mr-2" />
												Als Standard setzen
											</DropdownMenuItem>
											<DropdownMenuSeparator />
											<DropdownMenuItem
												onClick={() =>
													handleDeleteLocation(
														location.id
													)
												}
												className="text-red-600 focus:text-red-600"
												data-testid={`location-delete-${location.id}`}
											>
												<Trash2 className="h-4 w-4 mr-2" />
												Löschen
											</DropdownMenuItem>
										</>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
				))}
			</div>

			<Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Neuen Standort erstellen</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 pt-2">
						<Input
							value={newLocationName}
							onChange={(e) => setNewLocationName(e.target.value)}
							placeholder="Name des Standorts"
							onKeyDown={(e) =>
								e.key === "Enter" && handleAddLocation()
							}
							autoFocus
						/>
						<div className="flex justify-end gap-2">
							<Button
								variant="outline"
								onClick={() => setAddDialogOpen(false)}
							>
								Abbrechen
							</Button>
							<Button
								onClick={handleAddLocation}
								disabled={!newLocationName.trim()}
							>
								Erstellen
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};
